import {
  Component,
  ChangeDetectionStrategy,
  signal,
  inject,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import {
  MCPServerApi,
  McpSettings,
  PatchMcpSettingsModeEnum,
} from '@tourfold/libraries/tf-api-client-public-v2';
import {
  resolveSettingsLoadErrorDetails,
  resolveSettingsProblemMessage,
  SettingsActions,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsInlineMessageContent,
  type SettingsLoadErrorDetails,
  SETTINGS_PROBLEM_TRANSLATION_KEYS,
  SETTINGS_SUBPROBLEM_TRANSLATION_KEYS,
} from '@tourfold/features/settings-ui';
import { from } from 'rxjs';

interface ModeOption {
  label: string;
  value: PatchMcpSettingsModeEnum;
}

const MCP_SETTINGS_FIELD_LABEL_KEYS: Readonly<Record<string, string>> = {
  mode: 'SETTINGS_MCP.ERROR.FIELD_MODE',
};

@Component({
  selector: 'tf-settings-page-mcp',
  imports: [
    CommonModule,
    FormsModule,
    ClipboardModule,
    TranslateModule,
    ButtonModule,
    SelectModule,
    TooltipModule,
    SettingsActions,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './settings-mcp.component.html',
  styleUrl: './settings-mcp.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class McpSettingsComponent implements OnInit {
  private readonly mcpSettingsApi = inject(MCPServerApi);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly settings = signal<McpSettings | null>(null);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<SettingsInlineMessageContent | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly saveError = signal<SettingsInlineMessageContent | null>(null);
  readonly selectedMode = signal<PatchMcpSettingsModeEnum>(PatchMcpSettingsModeEnum.Off);
  readonly urlCopied = signal(false);

  readonly modeOptions = signal<ModeOption[]>([]);

  ngOnInit(): void {
    this.initModeOptions();
    this.loadSettings();
  }

  private initModeOptions(): void {
    this.modeOptions.set([
      {
        label: this.translateService.instant('SETTINGS_MCP.MODE_READ_WRITE'),
        value: PatchMcpSettingsModeEnum.ReadWrite,
      },
      {
        label: this.translateService.instant('SETTINGS_MCP.MODE_READ_ONLY'),
        value: PatchMcpSettingsModeEnum.ReadOnly,
      },
      {
        label: this.translateService.instant('SETTINGS_MCP.MODE_OFF'),
        value: PatchMcpSettingsModeEnum.Off,
      },
    ]);
  }

  private loadSettings(): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.saveError.set(null);
    from(this.mcpSettingsApi.getSettings1())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.settings.set(settings);
          this.selectedMode.set(settings.mode);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
          this.isLoading.set(false);
        },
        error: (error: unknown) => {
          void this.handleLoadError(error, hadLoadedOnce);
        },
      });
  }

  onModeChange(mode: PatchMcpSettingsModeEnum | null): void {
    if (mode) {
      this.selectedMode.set(mode);
      this.saveError.set(null);
    }
  }

  onUrlCopied(): void {
    this.urlCopied.set(true);
    setTimeout(() => this.urlCopied.set(false), 2000);
  }

  cancel(): void {
    this.loadSettings();
  }

  retryLoad(): void {
    this.loadSettings();
  }

  clearSaveError(): void {
    this.saveError.set(null);
  }

  save(): void {
    if (!this.settings()) {
      return;
    }

    this.isSaving.set(true);
    this.saveError.set(null);
    from(
      this.mcpSettingsApi.patchSettings1({
        patchMcpSettings: { mode: this.selectedMode() },
      })
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.settings.set(settings);
          this.selectedMode.set(settings.mode);
          this.loadError.set(null);
          this.saveError.set(null);
          this.isSaving.set(false);
        },
        error: (error: unknown) => {
          void this.handleSaveError(error);
        },
      });
  }

  private async handleSaveError(error: unknown): Promise<void> {
    this.saveError.set(await this.resolveErrorMessage(error, 'SETTINGS_MCP.ERROR.SAVE_FAILED'));
    this.isSaving.set(false);
  }

  private async handleLoadError(error: unknown, hadLoadedOnce: boolean): Promise<void> {
    const [message, details] = await Promise.all([
      this.resolveErrorMessage(error, 'SETTINGS_MCP.ERROR.LOAD_FAILED'),
      resolveSettingsLoadErrorDetails(error),
    ]);
    if (hadLoadedOnce) {
      this.saveError.set(message);
      this.loadError.set(null);
      this.loadErrorDetails.set(null);
      this.initialLoadFailed.set(false);
    } else {
      this.loadError.set(message);
      this.loadErrorDetails.set(details);
      this.initialLoadFailed.set(true);
    }
    this.isLoading.set(false);
  }

  private async resolveErrorMessage(
    error: unknown,
    fallbackKey: string
  ): Promise<SettingsInlineMessageContent> {
    return resolveSettingsProblemMessage(error, this.translateService, {
      fallbackKey,
      problemTranslationKeys: SETTINGS_PROBLEM_TRANSLATION_KEYS,
      subproblemTranslationKeys: SETTINGS_SUBPROBLEM_TRANSLATION_KEYS,
      fieldLabelKeys: MCP_SETTINGS_FIELD_LABEL_KEYS,
    });
  }
}
